import { IconCross } from '../../icons/IconCross'
import { IOption } from '../../forms/search-input/BpSearchInput'
import React, { useMemo } from 'react'
import ContentLoader from 'react-content-loader'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { FILTER_ICONS } from '../../icons/filters/FilterIcons'

interface FilterChipsProps {
  items: {
    field: string
    title: string
    value: any
    data?: IOption[]
    clearable?: boolean
    reversible?: boolean
    reversed?: boolean
    type?: 'text' | 'select' | 'multiselect'
  }[]
  clearCallBack?: (field: string) => void
  reverseCallBack?: (field: string) => void
  applyCallBack?: () => void
}

const ChipIcon = ({ field }: { field: string }) => {
  const Icon = FILTER_ICONS[field]

  return Icon ? (
    <span className={'mr-[6px] flex w-[16px] shrink-0 items-center text-aqua-300'}>
      <Icon />
    </span>
  ) : null
}

const ChipValues = ({ values }: { values: string[] }) => {
  const [first, ...rest] = values

  return (
    <div className={'flex items-center gap-[4px] text-[14px] text-focus-500'}>
      <Tooltip title={first}>
        <div className={'max-w-[175px] truncate'}>{first}</div>
      </Tooltip>
      {rest.length > 0 && (
        <Tooltip title={values.join(', ')}>
          <span className={'text-[14px] text-[#A89ABB]'}>+{rest.length}</span>
        </Tooltip>
      )}
    </div>
  )
}

const ReverseButton = ({ reversed, onToggle }: { reversed?: boolean; onToggle: () => void }) => {
  const { t } = useTranslation()

  return (
    <div className={'flex items-center'}>
      <button
        className={
          'h-[100%] whitespace-nowrap pl-[4px] font-medium uppercase text-focus hover:text-default'
        }
        onClick={onToggle}
      >
        {reversed ? t('buttons.isNot') : t('buttons.is')}
      </button>
    </div>
  )
}

export const FilterChips = ({
  items,
  clearCallBack,
  reverseCallBack,
  applyCallBack,
}: FilterChipsProps) => {
  const { t } = useTranslation()
  const chips = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        clearable: item.clearable !== false,
        type: item.type ? item.type : 'text',
      })),
    [items],
  )

  return (
    <div className={'flex cursor-default flex-wrap gap-y-[12px] gap-x-[24px]'}>
      {chips
        .filter((chip) => chip.value)
        .map((chip, index) =>
          chip.type === 'text' ? (
            <div key={index} className={'flex items-center'}>
              <ChipIcon field={chip.field} />
              <div className={'whitespace-nowrap text-[14px] text-aqua-300'}>{chip.title}</div>
              {chip.reversible && (
                <ReverseButton
                  reversed={chip.reversed}
                  onToggle={() => reverseCallBack && reverseCallBack(chip.field)}
                />
              )}
              <div
                className={
                  'ml-[8px] flex overflow-hidden rounded-[6px] bg-white shadow-outline shadow-focus/[0.65]'
                }
              >
                <div className={'flex gap-[4px] px-[8px] py-[5px]'}>
                  <ChipValues values={`${chip.value}`.split(',')} />
                </div>
                {chip.clearable && (
                  <div className={'flex items-center border-l-[2px] border-focus/[0.1]'}>
                    <button
                      className={'h-[100%] w-[32px] px-[8px] hover:bg-focus/[0.1]'}
                      onClick={() => clearCallBack && clearCallBack(chip.field)}
                    >
                      <IconCross color={'#6684A7'} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : chip.data?.length ? (
            <div key={index} className={'flex items-center'}>
              <ChipIcon field={chip.field} />
              <div className={'whitespace-nowrap text-[14px] text-aqua-300'}>{chip.title}</div>
              {chip.reversible && (
                <ReverseButton
                  reversed={chip.reversed}
                  onToggle={() => reverseCallBack && reverseCallBack(chip.field)}
                />
              )}
              <div
                className={
                  'ml-[8px] flex overflow-hidden rounded-[6px] bg-white shadow-outline shadow-focus/[0.65]'
                }
              >
                <div className={'flex gap-[4px] px-[8px] py-[5px]'}>
                  {chip.type === 'select' ? (
                    <div className={'text-[14px] text-focus-500'}>
                      {chip.data
                        .filter((option) => option.value === chip.value)
                        .map((option, index) => (
                          <span key={index}>{option.text}</span>
                        ))}
                    </div>
                  ) : (
                    <ChipValues
                      values={`${chip.value}`
                        .split(',')
                        .map((value) => chip.data?.find((option) => option.value === value)?.text)
                        .filter((text): text is string => !!text)}
                    />
                  )}
                </div>
                {chip.clearable && (
                  <div className={'flex items-center border-l-[2px] border-focus/[0.1]'}>
                    <button
                      className={'h-[100%] w-[32px] px-[8px] hover:bg-focus/[0.1]'}
                      onClick={() => {
                        clearCallBack && clearCallBack(chip.field)
                      }}
                    >
                      <IconCross color={'#6684A7'} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <ContentLoader
              key={index}
              backgroundColor='#f3f3f3'
              className={'h-[37px] w-[150px]'}
              foregroundColor='#ecebeb'
              speed={2}
            >
              <rect height='100%' rx='8' ry='8' width='100%' x='0' y='0' />
            </ContentLoader>
          ),
        )}
      {chips.filter((chip) => chip.value).filter((chip) => chip.clearable).length > 1 && (
        <button
          className={'px-[8px] text-[14px] text-[#FF5778]'}
          onClick={() => {
            clearCallBack &&
              chips.filter((chip) => chip.clearable).forEach((chip) => clearCallBack(chip.field))
            applyCallBack && applyCallBack()
          }}
        >
          {t('buttons.clearSelected')}
        </button>
      )}
    </div>
  )
}
